import { useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

const sortOptions = [
  { key: 'hot', label: '热度优先' },
  { key: 'new', label: '最新更新' },
  { key: 'rating', label: '评分最高' },
  { key: 'completed', label: '只看完结' },
];

export default function ComicsPage() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get('category') || '全部';
  const activeSort = params.get('sort') || 'hot';
  const [page, setPage] = useState(1);
  const perPage = 12;

  const filtered = useMemo(() => {
    let list = activeCat === '全部' ? [...comics] : comics.filter((c) => c.category === activeCat);
    if (activeSort === 'new') list = list.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0) || b.lastUpdated.localeCompare(a.lastUpdated));
    else if (activeSort === 'rating') list = list.sort((a, b) => Number(b.rating) - Number(a.rating));
    else if (activeSort === 'completed') list = list.filter((c) => c.status === '已完结');
    else list = list.sort((a, b) => Number(b.views.replace(/[万亿万]/g, '')) - Number(a.views.replace(/[万亿万]/g, '')) || Number(b.rating) - Number(a.rating));
    return list;
  }, [activeCat, activeSort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentList = filtered.slice((page - 1) * perPage, page * perPage);

  const setFilter = (cat: string, sort?: string) => {
    const next = new URLSearchParams();
    if (cat !== '全部') next.set('category', cat);
    if (sort) next.set('sort', sort);
    setParams(next, { replace: false });
    setPage(1);
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <nav aria-label="面包屑" className="mb-5 text-sm text-foreground-500">
          <ol className="flex flex-wrap items-center gap-1.5">
            <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li><span className="text-foreground-700">全部漫画</span></li>
          </ol>
        </nav>

        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl text-foreground-900 md:text-3xl">全部漫画</h1>
            <p className="mt-2 text-sm text-foreground-600">按分类浏览全部漫画列表，共收录 {comics.length} 部作品，轻松找到你想追的漫画。</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {sortOptions.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setFilter(activeCat, s.key)}
                className={`rounded-full px-4 py-2 text-sm whitespace-nowrap ${activeSort === s.key ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </header>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500">分类：</span>
          {['全部', ...categories.map((c) => c.name)].map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setFilter(cat, activeSort)}
              className={`rounded-full px-3.5 py-1.5 text-sm whitespace-nowrap ${activeCat === cat ? 'bg-secondary-500 text-background-50' : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'}`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
          {currentList.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-background-100 text-foreground-700 hover:bg-background-200 disabled:opacity-40"
              aria-label="上一页"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                className={`h-10 w-10 rounded-md text-sm ${page === n ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'}`}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-background-100 text-foreground-700 hover:bg-background-200 disabled:opacity-40"
              aria-label="下一页"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        )}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: '全部漫画 - 4399漫画网',
              description: '按分类浏览全部漫画列表，涵盖热血战斗、恋爱校园、玄幻仙侠等八大分类。',
              isPartOf: { '@type': 'WebSite', name: '4399漫画网' },
            }),
          }}
        />
      </div>
    </Layout>
  );
}